import { isChunkLoadError, type ErrorContext } from '@supply/contracts'
import * as React from 'react'

import { Loading } from '@/components/states'
import { buildErrorContext, logError, reloadForNewVersionOnce, setFatalShown } from '@/lib/error-capture'

import { ErrorScreen } from './ErrorScreen'

/**
 * Erro que quebra a renderização cai aqui, nunca em tela branca.
 *
 * - `tela-cheia`: o da raiz (`main.tsx`), por fora de tema, sessão e router —
 *   pega o que derrubou o shell inteiro.
 * - `area`: o do conteúdo (`App.tsx`), que mantém menu e topo de pé. Troca de
 *   rota (`resetKey`) limpa o erro.
 *
 * Chunk que sumiu depois de deploy recarrega a página uma vez antes.
 */

interface Props {
  readonly variant: 'tela-cheia' | 'area'
  readonly resetKey?: string
  readonly onHome?: () => void
  readonly children: React.ReactNode
}

type State =
  | { kind: 'ok' }
  | { kind: 'atualizando' }
  | { kind: 'erro'; ctx: ErrorContext | null }

export class ErrorBoundary extends React.Component<Props, State> {
  override state: State = { kind: 'ok' }

  static getDerivedStateFromError(): State {
    return { kind: 'erro', ctx: null }
  }

  override componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    if (isChunkLoadError(error)) {
      logError('carregamento', error)
      if (reloadForNewVersionOnce()) return this.setState({ kind: 'atualizando' })
    } else {
      logError('tela', error)
    }
    setFatalShown(true)
    this.setState({ kind: 'erro', ctx: buildErrorContext(error, info.componentStack) })
  }

  override componentDidUpdate(previous: Props): void {
    if (previous.resetKey !== this.props.resetKey && this.state.kind === 'erro') this.reset()
  }

  override componentWillUnmount(): void {
    if (this.state.kind === 'erro' && this.state.ctx) setFatalShown(false)
  }

  reset = (): void => {
    if (this.state.kind === 'erro' && this.state.ctx) setFatalShown(false)
    this.setState({ kind: 'ok' })
  }

  override render(): React.ReactNode {
    const { variant, onHome, children } = this.props
    const state = this.state
    if (state.kind === 'ok') return children
    if (state.kind === 'atualizando' || !state.ctx) {
      return <Loading label={state.kind === 'atualizando' ? 'Atualizando para a versão nova…' : 'Carregando...'} />
    }
    return <ErrorScreen ctx={state.ctx} variant={variant} onRetry={this.reset} onHome={onHome} />
  }
}
